<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-card" @click.stop>
      <button class="modal-close" type="button" @click="$emit('close')">
        <InvIcon name="chevron" style="transform: rotate(0)" />
        <span class="sr">Close</span>
      </button>

      <div class="modal-head">
        <TokenLogo :src="tokenImage" :name="tokenName" :size="48" />
        <div class="head-body">
          <p class="head-name">{{ tokenName }}</p>
          <p class="head-date">{{ dateLabel }}</p>
        </div>
        <span class="status-badge" :style="{ color: statusColor, background: statusBg }">
          {{ statusLabel }}
        </span>
      </div>

      <div class="detail-grid">
        <div class="detail-item">
          <span class="detail-label">Order Type</span>
          <span class="detail-value">{{ order.type || "—" }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Tokens</span>
          <span class="detail-value">{{ tokensLabel }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Price</span>
          <span class="detail-value">{{ priceLabel }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Amount</span>
          <span class="detail-value">{{ amountLabel }}</span>
        </div>
      </div>

      <div v-if="cancellable" class="modal-actions">
        <template v-if="!confirming">
          <button class="btn-cancel" type="button" :disabled="loading" @click="confirming = true">
            Cancel Order
          </button>
        </template>
        <template v-else>
          <p class="confirm-text">Cancel this order? This can't be undone.</p>
          <div class="confirm-row">
            <button class="btn-keep" type="button" :disabled="loading" @click="confirming = false">
              Keep Order
            </button>
            <button class="btn-confirm" type="button" :disabled="loading" @click="$emit('cancel', order)">
              <span v-if="loading" class="spinner"></span>
              <span v-else>Yes, Cancel</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import TokenLogo from "@/components/invest/TokenLogo.vue";
import InvIcon from "@/components/invest/InvIcon.vue";
import * as inv from "@/utils/invest.js";

export default {
  name: "InvestOrderDetailModal",
  components: { TokenLogo, InvIcon },
  props: {
    order: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  emits: ["close", "cancel"],
  data() {
    return { confirming: false };
  },
  computed: {
    token() {
      return this.order.token || {};
    },
    tokenName() {
      return this.token.name || this.token.symbol || "—";
    },
    tokenImage() {
      return this.token.image || "";
    },
    currency() {
      return this.token.currency || "NGN";
    },
    dateLabel() {
      return inv.formatDayMonthYear(this.order.createdAt);
    },
    statusLabel() {
      return inv.orderStatusLabel(this.order.status);
    },
    statusColor() {
      return inv.orderStatusColor(this.order.status);
    },
    statusBg() {
      const c = this.statusColor.replace("#", "");
      return `rgba(${parseInt(c.substring(0, 2), 16)}, ${parseInt(
        c.substring(2, 4),
        16
      )}, ${parseInt(c.substring(4, 6), 16)}, 0.1)`;
    },
    tokensLabel() {
      return inv.shareCount(inv.orderTokens(this.order));
    },
    priceLabel() {
      return inv.money(this.order.price, this.currency, 2);
    },
    amountLabel() {
      return inv.money(inv.orderAmountValue(this.order), this.currency, 2);
    },
    cancellable() {
      // Only pending/active orders can be cancelled.
      return this.order.status === "Active" || this.order.status === "Pending";
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 18, 64, 0.5);
}

.modal-card {
  position: relative;
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}

.modal-close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #f5f7fa;
  color: #4f5877;
  font-size: 16px;
  cursor: pointer;

  .sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}

.modal-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 30px;
  margin-bottom: 20px;
}

.head-body {
  flex: 1;
  min-width: 0;
}

.head-name {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #001240;
}

.head-date {
  margin: 2px 0 0;
  font-size: 11px;
  color: #8791a7;
}

.status-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 16px;
  background: #f8f9fc;
  border-radius: 10px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  color: #8791a7;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #001240;
}

.modal-actions {
  margin-top: 20px;
}

.btn-cancel {
  width: 100%;
  padding: 12px;
  border: 1px solid #e84343;
  border-radius: 10px;
  background: #ffffff;
  color: #e84343;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: rgba(232, 67, 67, 0.06);
  }
}

.confirm-text {
  margin: 0 0 12px;
  font-size: 13px;
  color: #4f5877;
  text-align: center;
}

.confirm-row {
  display: flex;
  gap: 10px;
}

.btn-keep {
  flex: 1;
  padding: 12px;
  border: 1px solid #d0d5e0;
  border-radius: 10px;
  background: #ffffff;
  color: #4f5877;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-confirm {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: #e84343;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
